import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { auth, db, functions } from "../../firebase/firebase";
import { InterviewRequestSchema, nameofRequestSchemaCollection, nameofRequestSchemaDate, nameofRequestSchemaStatus, nameofRequestSchemaType, nameofRequestSchemaUserId, RequestStatus, RequestType, RequestView, WWCCRequestSchema, WWCCRequestView } from "../Types/RequestSchema";
import { httpsCallable } from "firebase/functions";

export async function getAllRequests(): Promise<RequestView[]> {
    const requestRef = collection(db, nameofRequestSchemaCollection);
    const requestQuery = query(requestRef, orderBy(nameofRequestSchemaDate, 'desc'), limit(25));
    const requests = await getDocs(requestQuery);
    const requestList: RequestView[] = [];
    requests.forEach((doc) => {
        requestList.push({
            Id: doc.id,
            UserId: doc.data().UserId,
            Date: doc.data().Date,
            Status: doc.data().Status,
            Type: doc.data().Type
        } as RequestView);
    });

    return requestList;
}

export async function getAllActiveRequests(): Promise<RequestView[]> {
    const requestRef = collection(db, nameofRequestSchemaCollection);
    const requestSnapshot = query(requestRef, where(nameofRequestSchemaStatus, "==", RequestStatus.Pending), orderBy(nameofRequestSchemaDate, 'asc'));
    const requests = await getDocs(requestSnapshot);
    const requestList: RequestView[] = [];
    requests.forEach((doc) => {
        requestList.push({
            Id: doc.id,
            UserId: doc.data().UserId,
            Date: doc.data().Date,
            Status: doc.data().Status,
            Type: doc.data().Type
        } as RequestView);
    });

    return requestList;
}

export async function getRequestById(requestId: string): Promise<WWCCRequestView | RequestView | undefined> {
    const requestRef = doc(db, nameofRequestSchemaCollection, requestId);
    const requestDocs = await getDoc(requestRef);
    
    if (!requestDocs.exists()) {
        return undefined;
    }

    if (requestDocs.data()?.Type === RequestType.WWCC) {
        return {
            Id: requestDocs.id,
            UserId: requestDocs.data().UserId,
            Date: requestDocs.data().Date,
            Status: requestDocs.data().Status,
            Type: requestDocs.data().Type,
            WWCC: requestDocs.data().WWCC,
        } as WWCCRequestView;
    }
    else if (requestDocs.data()?.Type === RequestType.Interview) {
        return {
            Id: requestDocs.id,
            UserId: requestDocs.data().UserId,
            Date: requestDocs.data().Date,
            Status: requestDocs.data().Status,
            Type: requestDocs.data().Type
        } as RequestView;
    }
}

export async function getWWCCRequest(): Promise<WWCCRequestSchema[]> {
    const requestRef = collection(db, nameofRequestSchemaCollection);
    const requestSnapshot = await query(requestRef, where(nameofRequestSchemaUserId, "==", auth.currentUser?.uid), where(nameofRequestSchemaType, "==", RequestType.WWCC));
    const requestList: WWCCRequestSchema[] = [];
    const requestDocs = await getDocs(requestSnapshot);
    requestDocs.forEach((doc) => {
        requestList.push(doc.data() as WWCCRequestSchema);
    });

    return requestList;
}

export async function getInterviewRequest(): Promise<InterviewRequestSchema[]> {
    const requestRef = collection(db, nameofRequestSchemaCollection);
    const requestSnapshot = await query(requestRef, where(nameofRequestSchemaUserId, "==", auth.currentUser?.uid), where(nameofRequestSchemaType, "==", RequestType.Interview));
    const requestList: InterviewRequestSchema[] = [];
    const requestDocs = await getDocs(requestSnapshot);
    requestDocs.forEach((doc) => {
        requestList.push(doc.data() as InterviewRequestSchema);
    });

    return requestList;
}

export async function createWWCCRequest(WWCC: string): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const createWWCCRequestFunction = httpsCallable(functions, "createWWCCRequest");

    await createWWCCRequestFunction({
        UserId: auth.currentUser?.uid,
        WWCC: WWCC,
    });
}

export async function createInterviewRequest(): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const createInterviewRequestFunction = httpsCallable(functions, "createInterviewRequest");

    await createInterviewRequestFunction();
}

export async function createSchoolRequest(SchoolId: string): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const createSchoolRequestFunction = httpsCallable(functions, "createSchoolRequest");

    await createSchoolRequestFunction({
        UserId: auth.currentUser?.uid,
        SchoolId: SchoolId,
    });
}

export async function approveRequest(requestId: string): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const approveRequestFunction = httpsCallable(functions, "approveRequest");

    await approveRequestFunction({
        requestId: requestId,
    });
}

export async function rejectRequest(requestId: string): Promise<void> {
    if (auth.currentUser == null) {
        throw new Error("No user is currently signed in.");
    }

    const rejectRequestFunction = httpsCallable(functions, "rejectRequest");

    await rejectRequestFunction({
        requestId: requestId,
    });
}